import * as d3 from 'd3';
import _ from 'underscore';

import arcs, {BenchlingArc} from 'molbio/maps/circular/Arcs';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {AngledText, GenericArcDatum} from 'molbio/maps/circular/CircularMapTypedefs';
import {SegmentsRingData} from 'molbio/maps/circular/CircularTypedefs';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import SegmentsRingView, {SegmentRingSelection} from 'molbio/maps/circular/SegmentsRingView';
import getNucleotideAwareDisplayName from 'molbio/maps/getNucleotideAwareDisplayName';
import * as mapsutil from 'molbio/maps/MapsUtil';
import * as sequtil from 'molbio/SeqUtil';
import {NEAR_BLACK} from 'ui-library/Colors';

type Angles = GenericArcDatum & {
  clickEndAngle?: number;
};

interface LabeledSegmentsRingViewAttributes<TLayout extends RingLayout>
  extends RingComponentOptions<TLayout> {
  hasCircularLabel: boolean;
}

interface LabeledSegmentsRingView<
  TLayout extends RingLayout,
  TLabeledSegmentsRingData extends LabeledSegmentsRingData,
> {
  get<K extends keyof LabeledSegmentsRingViewAttributes<TLayout>>(
    key: K
  ): LabeledSegmentsRingViewAttributes<TLayout>[K];

  set<K extends keyof LabeledSegmentsRingViewAttributes<TLayout>>(
    attributeName: K,
    value: LabeledSegmentsRingViewAttributes<TLayout>[K],
    options?: Backbone.ModelSetOptions
  ): LabeledSegmentsRingView<TLayout, TLabeledSegmentsRingData>;

  set(
    obj: Partial<LabeledSegmentsRingViewAttributes<TLayout>>
  ): LabeledSegmentsRingView<TLayout, TLabeledSegmentsRingData>;
}

interface LabeledSegmentsRingData extends SegmentsRingData {
  displayLabel(): string;
  name(): string;
}

abstract class LabeledSegmentsRingView<
  TLayout extends RingLayout,
  TLabeledSegmentsRingData extends LabeledSegmentsRingData,
> extends SegmentsRingView<TLayout, TLabeledSegmentsRingData> {
  _allowLabels!: boolean;

  initialize(options: RingComponentOptions<TLayout>): void {
    super.initialize(options);
    this._allowLabels = false;
  }

  allowLabels(): boolean {
    return this._allowLabels;
  }

  handleHover(model: TLabeledSegmentsRingData, value: boolean): void {
    super.handleHover(model, value);
    const id = model.uid();
    const root = d3.select(this.el).selectAll('.individual-ring').filter(`[id='${id}']`);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    root.select('text').attr('font-weight', this.fontWeight);
  }

  handleText(selector: d3.Selection<SVGGElement, TLabeledSegmentsRingData, SVGGElement, unknown>): void {
    const visitedTexts: Array<AngledText> = [];

    const ref = this;
    selector.each(function (d: TLabeledSegmentsRingData) {
      const path = d3.select(this).select('path');
      const text = d3.select(this).select('text');
      const textBg = d3.select(this).select('.text-bg');
      const textPath = text.select('.text-path');

      const layer = d.get('annularLayer');
      const bounds = ref.layerBounds(layer);
      const radius = ref.baseLine(layer);

      const pathArc: BenchlingArc<unknown> = arcs.linearArc<unknown>();
      pathArc.reverse(false);

      const calculatedSize = ref.calcSize(d, layer, d.get('forceLabel'));

      const {angles} = calculatedSize;
      const {name} = calculatedSize;
      const {anglePadding} = calculatedSize;
      const {isOutside} = calculatedSize;

      let textAnchor = null;
      let startOffset = 0;

      if (name === '') {
        d.set('hasCircularLabel', false);

        if (this.style.display !== 'none') {
          this.style.display = 'none';
        }
      } else {
        if (this.style.display === 'none') {
          this.style.display = '';
        }

        d.set('hasCircularLabel', true);

        const targetAngle = (angles.startAngle + angles.endAngle) / 2;
        const visitedText: AngledText = {angle: targetAngle, orientation: null};
        const targetIndex = _.sortedIndex(visitedTexts, visitedText, (vt) => vt.angle);
        const middle = targetIndex - 1;
        const middle1 = (angles.startAngle + angles.endAngle) / 2;

        const potentialTexts = [];
        if (0 <= middle && middle < visitedTexts.length) {
          potentialTexts.push(visitedTexts[targetIndex - 1]);
        }
        if (targetIndex < visitedTexts.length) {
          potentialTexts.push(visitedTexts[targetIndex]);
        }

        const potentialText = _.min(potentialTexts, (vt) => Math.abs(vt.angle - visitedText.angle));
        if (potentialText != null && Math.abs(potentialText.angle - visitedText.angle) < (2 * Math.PI) / 36) {
          visitedText.orientation = potentialText.orientation;
        }
        visitedTexts.splice(targetIndex, 0, visitedText);

        const toBottom = Math.PI / 2 < middle1 && middle1 < (3 * Math.PI) / 2;
        if (visitedText.orientation == null) {
          visitedText.orientation = toBottom;
        }

        if (visitedText.orientation) {
          const pathArcR = radius + ref.layout.get('type').height() - 2;
          pathArc
            .startAngle(angles.startAngle)
            .endAngle(angles.endAngle - anglePadding)
            .reverse(true)
            // @ts-expect-error
            .radius(pathArcR);

          if (isOutside) {
            textAnchor = 'end';
            startOffset = pathArcR * (angles.endAngle - angles.startAngle);
          }
        } else {
          pathArc
            .startAngle(angles.startAngle + anglePadding)
            .endAngle(angles.endAngle)
            .radius(radius + 2);
        }

        const time = new Date().getTime();
        path.attr('d', pathArc).attr('id', `feature-${d.uid()}-${time}`);
        text.attr('fill', (d: TLabeledSegmentsRingData) => (isOutside ? NEAR_BLACK : ref.fontColor(d)));
        textPath
          .attr('xlink:href', `#feature-${d.uid()}-${time}`)
          // @ts-expect-error This either uses the null overload or the string | number | boolean overload but
          // it doesn't match any single overload so TS is mad
          .attr('text-anchor', textAnchor)
          .attr('startOffset', startOffset)
          .each(function (_: TLabeledSegmentsRingData): void {
            const node = d3.select(this);
            if (node.text() !== name) {
              d3.select(this).text(name);
            }
          });
        // .text(name) is slower since we do not change the text often.

        textBg.attr(
          'd',
          ((_) =>
            function (d: TLabeledSegmentsRingData) {
              if (isOutside) {
                const arc = d3.arc<TLabeledSegmentsRingData>();
                arc
                  .innerRadius(bounds.innerRadius)
                  .outerRadius(bounds.outerRadius)
                  .startAngle(angles.startAngle)
                  .endAngle(angles.clickEndAngle!);
                return arc(d);
              } else {
                return 'M 0 0';
              }
            })(this)
        );
      }
    });
  }

  cutoffRadius(): number {
    return this.model.get('minInnerRadius');
  }

  calcSize(
    d: TLabeledSegmentsRingData,
    layer: number,
    forceLabel: boolean
  ): {name: string; angles: Angles; anglePadding: number; isOutside: boolean} {
    const start = d.start();
    const end = d.end();
    let name = getNucleotideAwareDisplayName(d);
    let angles: Angles = this.getAngles(start, end);
    const radius = this.baseLine(layer);
    let textSliceAngle = radius !== 0 ? this.layout.get('type').width(name.length) / radius : 0;

    let anglePadding = (angles.endAngle - angles.startAngle - textSliceAngle) / 2;
    let isOutside = false;
    let isOptimal = true;

    // If there is less than 10px of padding on each side, we consider the label to be too large.
    if (anglePadding * radius < 10) {
      if (textSliceAngle + (angles.endAngle - angles.startAngle) > (2 * Math.PI * 3) / 4) {
        // If the annotation name is so long that we can't render the label next to the annotation, let's truncate!
        const space = ((angles.endAngle - angles.startAngle) * radius) / this.layout.get('type').width(1);
        name = `${d.displayLabel().slice(0, Math.max(0, space - 3))}...`;
        isOptimal = false;
        if (name === '...' || !isOptimal) {
          return {
            name: '',
            angles,
            anglePadding: 0,
            isOutside: false,
          };
        }
        textSliceAngle = this.layout.get('type').width(name.length) / radius;
        anglePadding = (angles.endAngle - angles.startAngle - textSliceAngle) / 2;
      } else {
        // Reasons for no label:
        //  - If the text spans more than 72 degrees outside the sequence.
        //  - Plasmid said no room for labels.

        if (!(this.allowLabels() || forceLabel)) {
          // or hideLongLabels and textSliceAngle > 2*Math.PI/5
          return {
            name: '',
            angles,
            anglePadding: 0,
            isOutside: false,
          };
        }
        // Otherwise, we show the label next to the annotation

        const startAngle = angles.endAngle + 5 / radius;
        angles = {
          startAngle,
          endAngle: startAngle + Math.min(textSliceAngle * 1.4, 2 * Math.PI - 1),
          clickEndAngle: startAngle + Math.min(textSliceAngle * 1.1, 2 * Math.PI - 1),
        };
        anglePadding = 0;
        isOutside = true;
      }
    }

    return {
      name,
      angles,
      anglePadding,
      isOutside,
    };
  }

  enterRing(
    enter: SegmentRingSelection<TLabeledSegmentsRingData>
  ): SegmentRingSelection<TLabeledSegmentsRingData> {
    super.enterRing(enter);
    const textG = enter.append<SVGGElement>('svg:g').classed('text-group', true).merge(enter);
    textG.append('svg:path').attr('stroke', 'none').attr('stroke-width', 1).attr('fill', 'none');

    const textBG = textG
      .append<SVGGElement>('svg:path')
      .classed('text-bg', true)
      .attr('fill', 'red')
      .attr('fill-opacity', 0);

    const text = textG
      .append('svg:text')
      .classed('ring-label', true)
      .attr('font-size', this.layout.get('type').fontSize)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .attr('font-weight', this.fontWeight)
      .attr('text-rendering', 'optimizeLegibility')
      .attr('pointer-events', 'none');

    text.append('svg:textPath').classed('text-path', true).attr('text-rendering', 'optimizeLegibility');

    this.registerPieceHandlers(textBG);

    return textG;
  }

  eachRing(root: SegmentRingSelection<TLabeledSegmentsRingData>): void {
    super.eachRing(root);
    this.handleText(root.select('.text-group'));
  }

  updateLayers(data: Array<TLabeledSegmentsRingData>): void {
    const length = this.model.get('sequence').length();
    const tryLabels = this.layout.get('inlineLabelsVisible') ? [true, false] : [false];

    for (this._allowLabels of tryLabels) {
      const startFn = (
        model: TLabeledSegmentsRingData
      ): {
        model: TLabeledSegmentsRingData;
        pos: number;
      } => ({
        model,
        pos: model.layerStart(),
      });

      const endFnWithLabel = (
        model: TLabeledSegmentsRingData,
        info: {layer: number},
        forceLabel: boolean
      ): {
        model: TLabeledSegmentsRingData;
        pos: number;
        calcSize: {name: string; angles: Angles; anglePadding: number; isOutside: boolean};
      } => {
        let end;
        const calculatedSize = this.calcSize(model, info.layer, forceLabel);
        if (calculatedSize.isOutside) {
          end = sequtil.mod(Math.ceil(this.angle.invert(calculatedSize.angles.endAngle)), length);
        } else {
          end = model.end();
        }

        return {
          model,
          pos: end,
          calcSize: calculatedSize,
        };
      };

      const endFn = (
        model: TLabeledSegmentsRingData,
        info: {layer: number}
      ): {model: TLabeledSegmentsRingData; pos: number} => {
        if (this._allowLabels) {
          return endFnWithLabel(model, info, false);
        } else {
          return {
            model,
            pos: model.layerEnd(),
          };
        }
      };

      const layerData = mapsutil.getLayers(data, {
        startFn,
        endFn,
        top: true,
        sequence: this.model.get('sequence'),
      });
      const maxLayer = layerData.max;
      for (const layerInfo of layerData.layers) {
        const {layer} = layerInfo;
        const {model} = layerInfo;
        model.set({annularLayer: layer, forceLabel: false});

        if (!this._allowLabels && this.layout.get('inlineLabelsVisible')) {
          const endInfo = endFnWithLabel(model, {layer}, true);
          const nextSibling = layerData.siblingMap[model.cid];
          const targetEnd = startFn(nextSibling).pos;
          const modelStart = startFn(model).pos;
          const modelEnd = endInfo.pos;
          const {calcSize} = endInfo;
          // If the label doesn't cut through targetEnd, all is well.
          // + 1 since targetEnd might correspond to the model's own start.
          if (calcSize.isOutside && !sequtil.baseInRange(targetEnd, modelStart + 1, modelEnd)) {
            model.set({forceLabel: true});
          }
        }
      }

      this.storeBounds(maxLayer + 1);
      if (this.layerBounds(maxLayer).innerRadius > this.cutoffRadius()) {
        break;
      }
    }

    this.recomputeLayers = false;
  }
}

export default LabeledSegmentsRingView;
